import { PropsWithChildren } from 'react';
import { useModal } from '@ebay/nice-modal-react';
import { useDidUpdate, usePreservedCallback } from '@toss/react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useAuth } from '@zep/auth';
import { useDialog } from '@zep/dialog';
import { CountryCode } from '@zep/types';
import { Button } from '@zep/ui';
import { PostBillingCardCallBack } from '../../../../services';
import { createPaymentFailureDialog } from '../../payment-tracking/PaymentFailureContent';
import { PaypleProvider, PaypleWidgetDefaultPayload, usePayple } from '../hooks';
import { InputCardNameModal } from '../paymentForm';
import { makeRedirectUrl } from '../subscriptionUtil';
const PaypleChangePaymentButtonInner = (props: PropsWithChildren<{
  spaceId?: number;
  spaceHashId: string;
  paymentFlow?: 'card_registration' | 'card_change';
}>) => {
  const {
    t
  } = useTranslation(['subscription']);
  const router = useRouter();
  const {
    user
  } = useAuth();
  const dialog = useDialog();
  const {
    showWidget,
    isFailure,
    isSuccess,
    failMessage,
    failureMetadata,
    failureCount
  } = usePayple();
  const inputCardNameModal = useModal(InputCardNameModal);
  const onSuccessRegisterBilling = usePreservedCallback(() => {
    inputCardNameModal.show({
      spaceHashId: props.spaceHashId,
      onSuccess() {
        inputCardNameModal.remove();
        router.reload();
      }
    }).catch(console.error);
  });
  const onFailRegisterBilling = usePreservedCallback((message: string) => {
    const failureDialog = props.paymentFlow ? createPaymentFailureDialog(dialog, {
      ...failureMetadata,
      // URL 쿼리는 사용자가 고칠 수 있다. 화면이 확실히 아는 값을 마지막에 둬 덮이지 않게 한다.
      payment_flow: props.paymentFlow,
      pg_provider: 'payple',
      space_id: props.spaceId ?? failureMetadata.space_id
    }) : dialog;
    void failureDialog.alert({
      title: t('subscription:SpaceDetail.changeCardModal.description'),
      content: t('subscription:SpaceDetail.changeCardModal.reason', {
        message: message ?? 'Processing Failure'
      })
    });
  });
  useDidUpdate(() => {
    if (isSuccess) {
      onSuccessRegisterBilling();
    }
  }, [isSuccess, onSuccessRegisterBilling]);
  useDidUpdate(() => {
    if (isFailure) {
      onFailRegisterBilling(failMessage);
      return;
    }
  }, [isFailure, failMessage, failureCount, onFailRegisterBilling]);
  return <Button variant={'sub'} size={'xs'} onClick={() => showWidget({
    ...PaypleWidgetDefaultPayload,
    type: 'subscription',
    spaceHashId: props.spaceHashId,
    callbackUrl: PostBillingCardCallBack,
    playerEmail: user?.snsId ?? '',
    failRedirectUrl: makeRedirectUrl(router, {
      isBillingRegister: false
    }),
    successRedirectUrl: makeRedirectUrl(router, {
      isBillingRegister: true
    })
  })} data-sentry-element="Button" data-sentry-component="PaypleChangePaymentButtonInner" data-sentry-source-file="PaypleChangePaymentButton.tsx">
      {props.children}
    </Button>;
};
export const PaypleChangePaymentButton = (props: PropsWithChildren<{
  isUpdated: boolean;
  paymentFlow?: 'card_registration' | 'card_change';
  spaceId?: number;
  spaceHashId: string;
  countryCode: CountryCode;
}>) => {
  return <PaypleProvider data-sentry-element="PaypleProvider" data-sentry-component="PaypleChangePaymentButton" data-sentry-source-file="PaypleChangePaymentButton.tsx">
      <PaypleChangePaymentButtonInner spaceHashId={props.spaceHashId} spaceId={props.spaceId} paymentFlow={props.paymentFlow} data-sentry-element="PaypleChangePaymentButtonInner" data-sentry-source-file="PaypleChangePaymentButton.tsx">
        {props.children}
      </PaypleChangePaymentButtonInner>
    </PaypleProvider>;
};